import React, { useState, useRef, useEffect, useCallback } from 'react';
import { WallPost, wallPostsApi } from '../../../api/wallPostsApi';
import { getAvatarUrl, getMediaUrl, getPostImageUrls } from '../../../utils/avatarUtils';
import ImageViewer from './ImageViewer';
import PostParticipantsBottomSheet from './PostParticipantsBottomSheet';
import styles from './Post.module.css';

interface PostProps {
  post: WallPost;
  onLike: (postId: number) => void;
  onDelete: (postId: number) => void;
  onEdit?: (post: WallPost) => void;
  currentUserId: number;
  onUserClick?: (userId: number) => void;
  onSiteClick?: (siteId: number) => void;
  hideMenuButton?: boolean;
}

const Post: React.FC<PostProps> = ({ post, onLike, onDelete, onEdit, currentUserId, onUserClick, onSiteClick, hideMenuButton = false }) => {
  const [showComments, setShowComments] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
  const [sourceImageElement, setSourceImageElement] = useState<HTMLElement | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [touchStartX, setTouchStartX] = useState<number | null>(null);
  const [touchStartY, setTouchStartY] = useState<number | null>(null);
  const [touchEndX, setTouchEndX] = useState<number | null>(null);
  const [touchEndY, setTouchEndY] = useState<number | null>(null);
  const [isSwiping, setIsSwiping] = useState(false);
  const [viewsCount, setViewsCount] = useState(post.views || 0);
  const [showParticipantsSheet, setShowParticipantsSheet] = useState(false);
  const [isVerticalImage, setIsVerticalImage] = useState(false);
  const [showTextOverlay, setShowTextOverlay] = useState(true); // Показываем текст по умолчанию
  const [fullLoadedIndices, setFullLoadedIndices] = useState<Set<number>>(new Set()); // Progressive loading: какие full изображения загружены
  const imageRefs = useRef<(HTMLImageElement | null)[]>([]);
  const hasRegisteredView = useRef(false);
  
  const minSwipeDistance = 50;

  // Регистрируем просмотр поста при первом отображении
  useEffect(() => {
    if (!hasRegisteredView.current && post.id) {
      hasRegisteredView.current = true;
      // Регистрируем просмотр асинхронно, не блокируя рендер
      wallPostsApi.registerView(post.id).then((result) => {
        if (result.isNewView) {
          // Обновляем счетчик просмотров если это новый просмотр
          setViewsCount((prev) => prev + 1);
        }
      }).catch(() => {
        // Игнорируем ошибки регистрации просмотра
      });
    }
  }, [post.id]);
  
  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEndX(null);
    setTouchEndY(null);
    setTouchStartX(e.targetTouches[0].clientX);
    setTouchStartY(e.targetTouches[0].clientY);
    setIsSwiping(false);
  };
  
  const onTouchMove = (e: React.TouchEvent) => {
    if (!touchStartX || !touchStartY) return;
    
    const currentX = e.targetTouches[0].clientX;
    const currentY = e.targetTouches[0].clientY;
    
    setTouchEndX(currentX);
    setTouchEndY(currentY);
    
    const deltaX = Math.abs(currentX - touchStartX);
    const deltaY = Math.abs(currentY - touchStartY);
    
    // Определяем, что это горизонтальный свайп (больше по X, чем по Y)
    if (deltaX > deltaY && deltaX > 10) {
      setIsSwiping(true);
      // Не используем preventDefault здесь, так как React события не поддерживают passive: false
      // Вместо этого полагаемся на CSS touch-action
    }
  };
  
  const onTouchEnd = () => {
    if (!touchStartX || !touchEndX || !touchStartY || !touchEndY) {
      setTouchStartX(null);
      setTouchStartY(null);
      setTouchEndX(null);
      setTouchEndY(null);
      setIsSwiping(false);
      return;
    }
    
    const distanceX = touchStartX - touchEndX;
    const distanceY = Math.abs(touchStartY - touchEndY);
    
    // Проверяем, что это горизонтальный свайп (горизонтальное движение больше вертикального)
    if (Math.abs(distanceX) > distanceY && Math.abs(distanceX) > minSwipeDistance) {
      const isLeftSwipe = distanceX > 0;
      const isRightSwipe = distanceX < 0;
      const images = post.images;
      
      if (images && images.length > 1) {
        if (isLeftSwipe) {
          setCurrentImageIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
        } else if (isRightSwipe) {
          setCurrentImageIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
        }
      }
    }
    
    setTouchStartX(null);
    setTouchStartY(null);
    setTouchEndX(null);
    setTouchEndY(null);
    setIsSwiping(false);
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'только что';
    if (diffMins < 60) return `${diffMins} мин назад`;
    if (diffHours < 24) return `${diffHours} ч назад`;
    if (diffDays < 7) return `${diffDays} дн назад`;
    
    return date.toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'short',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  const getUserInitials = (name: string): string => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getAvatarColor = (userId: number): string => {
    const colors = [
      '#4A90E2', '#50C878', '#FF6B6B', '#FFA500', 
      '#9B59B6', '#E74C3C', '#3498DB', '#2ECC71'
    ];
    return colors[userId % colors.length];
  };

  const avatarUrl = getAvatarUrl(post.userAvatar, 'small');
  const isOwnPost = post.userId === currentUserId;

  // Функция для парсинга и подсветки хештегов в тексте
  const renderContentWithHashtags = (content: string) => {
    if (!content) return null;
    
    // Регулярка для поиска хештегов
    const hashtagRegex = /(#[\wа-яёА-ЯЁ]+)/g;
    const parts = content.split(hashtagRegex);
    
    return parts.map((part, index) => {
      if (part.match(hashtagRegex)) {
        return (
          <span key={index} className={styles.hashtag}>
            {part}
          </span>
        );
      }
      return part;
    });
  };
  
  // Сбрасываем индекс изображения при изменении поста
  useEffect(() => {
    setCurrentImageIndex(0);
    setSelectedImageIndex(null);
    setSourceImageElement(null);
  }, [post.id]);

  // Сбрасываем флаги progressive loading при смене изображений поста
  useEffect(() => {
    setFullLoadedIndices(new Set());
  }, [post.images]);

  const imageList = React.useMemo(() => getPostImageUrls(post.images), [post.images]);

  // Учитываем уже загруженные full (кэш: onLoad может не сработать)
  useEffect(() => {
    if (!imageList.length) return;
    const checkComplete = () => {
      setFullLoadedIndices(prev => {
        let changed = false;
        const next = new Set(Array.from(prev));
        imageList.forEach((item, index) => {
          if (!item.hasProgressive) return;
          const el = imageRefs.current[index];
          if (el?.complete && el.naturalWidth > 0 && !next.has(index)) {
            next.add(index);
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    };
    const t1 = requestAnimationFrame(checkComplete);
    const t2 = setTimeout(checkComplete, 150);
    const t3 = setTimeout(checkComplete, 800);
    return () => {
      cancelAnimationFrame(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [imageList]);

  // Определяем, является ли изображение вертикальным (по миниатюре — быстрее)
  useEffect(() => {
    if (imageList.length === 0) {
      setIsVerticalImage(false);
      return;
    }
    const first = imageList[0];
    const imageUrl = first?.thumbUrl || first?.fullUrl;
    if (!imageUrl) {
      setIsVerticalImage(false);
      return;
    }
    const img = new Image();
    img.onload = () => setIsVerticalImage(img.height > img.width * 1.1);
    img.onerror = () => setIsVerticalImage(false);
    img.src = imageUrl;
  }, [imageList]);

  // Обработчик изменения индекса в ImageViewer
  const handleIndexChange = useCallback((index: number) => {
    // Синхронизируем индекс при листании в большом слайдере
    setCurrentImageIndex(index);
    setSelectedImageIndex(index);
  }, []);

  // Обработчик закрытия ImageViewer для одного изображения
  const handleCloseSingleImage = useCallback(() => {
    setSelectedImageIndex(null);
    setSourceImageElement(null);
  }, []);

  // Обработчик закрытия ImageViewer для слайдера
  const handleCloseSlider = useCallback(() => {
    // Сохраняем текущий индекс перед закрытием
    // Используем функциональное обновление для получения актуального значения
    setSelectedImageIndex((lastIndex) => {
      if (lastIndex !== null) {
        setCurrentImageIndex(lastIndex);
      }
      return null;
    });
    setSourceImageElement(null);
  }, []);

  // Шапка поста: для поста объекта главный — объект, автор второй строкой.
  // Для личного поста (без объекта) — автор, как раньше.
  const renderUserInfo = (isOverlay: boolean = false) => {
    if (post.siteId && post.siteName) {
      const siteAvatarUrl = getAvatarUrl(post.siteAvatar, 'small');
      return (
        <div className={`${styles.userInfo} ${isOverlay ? styles.userInfoOverlay : ''}`}>
          <div
            className={styles.avatar}
            onClick={() => onSiteClick && onSiteClick(post.siteId!)}
            style={{ cursor: onSiteClick ? 'pointer' : 'default' }}
          >
            {siteAvatarUrl ? (
              <>
                <img
                  src={siteAvatarUrl}
                  alt={post.siteName}
                  className={styles.avatarImage}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    const fallback = target.nextElementSibling as HTMLElement;
                    if (fallback) {
                      fallback.style.display = 'flex';
                    }
                  }}
                />
                <div className={styles.siteAvatarFallback} style={{ display: 'none' }}>
                  <i className="ri-building-2-line"></i>
                </div>
              </>
            ) : (
              <div className={styles.siteAvatarFallback} style={{ display: 'flex' }}>
                <i className="ri-building-2-line"></i>
              </div>
            )}
          </div>

          <div className={styles.userDetails}>
            <div className={styles.userNameRow}>
              <span
                className={styles.userName}
                onClick={() => onSiteClick && onSiteClick(post.siteId!)}
                style={{ cursor: onSiteClick ? 'pointer' : 'default' }}
              >
                {post.siteName}
              </span>
            </div>
            <span className={styles.postDate}>
              <span
                className={styles.authorLink}
                onClick={(e) => {
                  e.stopPropagation();
                  if (onUserClick) onUserClick(post.userId);
                }}
                style={{ cursor: onUserClick ? 'pointer' : 'default' }}
              >
                {post.userName}
              </span>
              {' · '}{formatDate(post.createdAt)}
            </span>
          </div>
        </div>
      );
    }

    return (
    <div
      className={`${styles.userInfo} ${isOverlay ? styles.userInfoOverlay : ''}`}
      onClick={() => onUserClick && onUserClick(post.userId)}
      style={{ cursor: onUserClick ? 'pointer' : 'default' }}
    >
      <div className={styles.avatar}>
        {avatarUrl ? (
          <>
            <img 
              src={avatarUrl || ''}
              alt={post.userName}
              className={styles.avatarImage}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                const fallback = target.nextElementSibling as HTMLElement;
                if (fallback) {
                  fallback.style.display = 'flex';
                }
              }}
            />
            <div 
              className={styles.avatarFallback}
              style={{ 
                backgroundColor: getAvatarColor(post.userId),
                display: 'none'
              }}
            >
              {getUserInitials(post.userName)}
            </div>
          </>
        ) : (
          <div 
            className={styles.avatarFallback}
            style={{ 
              backgroundColor: getAvatarColor(post.userId),
              display: 'flex'
            }}
          >
            {getUserInitials(post.userName)}
          </div>
        )}
      </div>

      <div className={styles.userDetails}>
        <div className={styles.userNameRow}>
          <span className={styles.userName}>{post.userName}</span>
          {post.siteName && (
            <span className={styles.siteBadge}>
              <i className="ri-building-line"></i>
              {post.siteName}
            </span>
          )}
        </div>
        <span className={styles.postDate}>{formatDate(post.createdAt)}</span>
      </div>
    </div>
    );
  };

  return (
    <div className={styles.post}>
      {/* Для вертикальных изображений скрываем обычный заголовок */}
      {!isVerticalImage && (
        <div className={styles.postHeader}>
          {renderUserInfo(false)}

          {isOwnPost && !hideMenuButton && (
            <div className={styles.menuContainer}>
              <button 
                className={styles.menuButton}
                onClick={() => setShowMenu(!showMenu)}
              >
                <i className="ri-more-fill"></i>
              </button>

              {showMenu && (
                <div className={styles.menuDropdown}>
                  {onEdit && (
                    <button
                      className={styles.menuItem}
                      onClick={() => { onEdit(post); setShowMenu(false); }}
                    >
                      <i className="ri-edit-line"></i>
                      Редактировать
                    </button>
                  )}
                  <button
                    className={styles.menuItem}
                    onClick={() => {
                      onDelete(post.id);
                      setShowMenu(false);
                    }}
                  >
                    <i className="ri-delete-bin-line"></i>
                    Удалить
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Для вертикальных изображений текст показываем внизу, для обычных - сверху */}
      {!isVerticalImage && (
        <div className={styles.postContent}>
          <p>{renderContentWithHashtags(post.content)}</p>
        </div>
      )}

      {imageList.length > 0 && (() => {
        const imagesCount = imageList.length;
        const thumbUrls = imageList.map((x) => x.thumbUrl);
        const fullUrls = imageList.map((x) => x.fullUrl);
        const shouldLoadFull = (i: number) => i === currentImageIndex || fullLoadedIndices.has(i);

        if (imagesCount === 1) {
          const { thumbUrl, fullUrl, hasProgressive } = imageList[0];
          if (!thumbUrl && !fullUrl) return null;

          return (
            <>
              <div className={`${styles.postImages} ${isVerticalImage ? styles.verticalImage : ''}`}>
                <div 
                  className={styles.imageWrapper}
                  onClick={(e) => {
                    const imgElement = (e.currentTarget.querySelector(`.${styles.postImageFull}`) || e.currentTarget.querySelector('img')) as HTMLElement | null;
                    setSourceImageElement(imgElement);
                    setSelectedImageIndex(0);
                  }}
                >
                  {hasProgressive ? (
                    <>
                      <img 
                        src={thumbUrl} 
                        alt=""
                        className={`${styles.postImage} ${styles.postImageThumb}`}
                        aria-hidden
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                      />
                      <img 
                        src={fullUrl} 
                        alt="Post image"
                        className={`${styles.postImage} ${styles.postImageFull} ${fullLoadedIndices.has(0) ? styles.postImageFullVisible : ''}`}
                        ref={(el) => {
                          if (el) {
                            imageRefs.current[0] = el;
                            if (el.complete && el.naturalWidth > 0) {
                              requestAnimationFrame(() => setFullLoadedIndices(prev => prev.has(0) ? prev : new Set(Array.from(prev).concat(0))));
                            }
                          }
                        }}
                        onLoad={() => requestAnimationFrame(() => setFullLoadedIndices(prev => prev.has(0) ? prev : new Set(Array.from(prev).concat(0))))}
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                      />
                    </>
                  ) : (
                    <img 
                      src={fullUrl || thumbUrl} 
                      alt="Post image"
                      className={styles.postImage}
                      ref={(el) => { if (el) imageRefs.current[0] = el; }}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                      }}
                    />
                  )}
                  {/* Для вертикальных изображений показываем информацию о пользователе поверх фото */}
                  {isVerticalImage && (
                    <div className={styles.imageOverlay}>
                      {renderUserInfo(true)}
                      {isOwnPost && !hideMenuButton && (
                        <div className={styles.menuContainerOverlay}>
                          <button 
                            className={styles.menuButtonOverlay}
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowMenu(!showMenu);
                            }}
                          >
                            <i className="ri-more-fill"></i>
                          </button>

                          {showMenu && (
                            <div className={styles.menuDropdownOverlay}>
                              {onEdit && (
                                <button
                                  className={styles.menuItem}
                                  onClick={() => { onEdit(post); setShowMenu(false); }}
                                >
                                  <i className="ri-edit-line"></i>
                                  Редактировать
                                </button>
                              )}
                              <button
                                className={styles.menuItem}
                                onClick={() => {
                                  onDelete(post.id);
                                  setShowMenu(false);
                                }}
                              >
                                <i className="ri-delete-bin-line"></i>
                                Удалить
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                    {/* Область с текстом и миниатюрами для вертикальных изображений */}
                    {isVerticalImage && (imagesCount > 1 || post.content) && (
                      <div className={styles.thumbnailsContainerOverlay}>
                        {/* Текст описания внутри области миниатюр */}
                        {post.content && showTextOverlay && (
                          <div className={styles.textOverlayInline}>
                            <p>{renderContentWithHashtags(post.content)}</p>
                          </div>
                        )}
                        {/* Горизонтальная строка с миниатюрами и кнопкой */}
                        <div className={styles.thumbnailsRow}>
                          {/* Контейнер для миниатюр - центрируется */}
                          <div className={styles.thumbnailsContainer}>
                            {/* Миниатюры - показываем только если больше одного изображения */}
                            {imagesCount > 1 && (
                              <>
                                {thumbUrls.map((thumbUrl, index) => (
                                  <img
                                    key={index}
                                    src={thumbUrl}
                                    alt={`Thumbnail ${index + 1}`}
                                    className={`${styles.thumbnailOverlay} ${index === currentImageIndex ? styles.thumbnailOverlayActive : ''}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setCurrentImageIndex(index);
                                      // При клике на миниатюру открываем большой слайдер
                                      const imgElement = imageRefs.current[index];
                                      if (imgElement) {
                                        setSourceImageElement(imgElement);
                                        setSelectedImageIndex(index);
                                      }
                                    }}
                                  />
                                ))}
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  {/* Кнопка участников - в углу фото, поверх изображения */}
                  {post.siteId && (
                    <button
                      className={styles.participantsButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowParticipantsSheet(true);
                      }}
                      title="Участники объекта"
                    >
                      <i className="ri-group-line"></i>
                    </button>
                  )}
                  {/* Кнопка показа/скрытия текста - справа, симметрично кнопке участников */}
                  {isVerticalImage && post.content && (
                    <button
                      className={styles.textToggleButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowTextOverlay(!showTextOverlay);
                      }}
                      title={showTextOverlay ? "Скрыть описание" : "Показать описание"}
                    >
                      <i className={showTextOverlay ? "ri-file-text-fill" : "ri-file-text-line"}></i>
                    </button>
                  )}
                </div>
              </div>
              
              {/* Для вертикальных изображений текст больше не показываем внизу - он в overlay */}
              
              {selectedImageIndex !== null && (
                <ImageViewer
                  images={fullUrls}
                  imagesThumb={thumbUrls}
                  initialIndex={selectedImageIndex}
                  onClose={handleCloseSingleImage}
                  sourceElement={sourceImageElement}
                  siteId={post.siteId}
                  onShowParticipants={() => setShowParticipantsSheet(true)}
                />
              )}
            </>
          );
        }
        
        // Несколько фото: миниатюры сразу у всех, full — только у текущего и уже открытых
        return (
          <>
            <div className={`${styles.postImageSlider} ${isVerticalImage ? styles.verticalImageSlider : ''}`}>
              {/* Главное фото */}
              <div 
                className={styles.mainImageContainer}
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
              >
                <div 
                  className={styles.mainImageSlider}
                  style={{
                    transform: `translateX(calc(-${currentImageIndex * 100}% + ${isSwiping && touchStartX !== null && touchEndX !== null && touchStartY !== null && touchEndY !== null ? 
                      (Math.abs(touchEndX - touchStartX) > Math.abs(touchEndY - touchStartY) ? `${touchEndX - touchStartX}px` : '0px') 
                      : '0px'}))`,
                    transition: isSwiping ? 'none' : 'transform 0.3s ease'
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    const imgElement = imageRefs.current[currentImageIndex];
                    if (imgElement) {
                      setSourceImageElement(imgElement);
                      setSelectedImageIndex(currentImageIndex);
                    }
                  }}
                >
                  {fullUrls.map((fullUrl, index) => {
                  const thumbUrl = thumbUrls[index];
                  const hasProgressive = imageList[index].hasProgressive;
                  const loadFull = shouldLoadFull(index);
                  return hasProgressive ? (
                    <div key={`post-img-${index}`} className={styles.mainImageWrapper}>
                      <img 
                        src={thumbUrl} 
                        alt=""
                        className={`${styles.mainImage} ${styles.postImageThumb}`}
                        aria-hidden
                        loading="eager"
                        ref={!loadFull ? (el) => { if (el) imageRefs.current[index] = el; } : undefined}
                      />
                      {loadFull && (
                        <img 
                          key={`full-${index}`}
                          ref={(el) => {
                            if (el) {
                              imageRefs.current[index] = el;
                              if (el.complete && el.naturalWidth > 0) {
                                requestAnimationFrame(() => setFullLoadedIndices(prev => prev.has(index) ? prev : new Set(Array.from(prev).concat(index))));
                              }
                            }
                          }}
                          src={fullUrl} 
                          alt={`Post image ${index + 1}`}
                          className={`${styles.mainImage} ${styles.postImageFull} ${fullLoadedIndices.has(index) ? styles.postImageFullVisible : ''}`}
                          loading="eager"
                          decoding="async"
                          onLoad={() => requestAnimationFrame(() => setFullLoadedIndices(prev => prev.has(index) ? prev : new Set(Array.from(prev).concat(index))))}
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.backgroundColor = 'var(--background-tertiary)';
                            (e.target as HTMLImageElement).style.display = 'block';
                          }}
                        />
                      )}
                    </div>
                  ) : (
                    <img 
                      key={`post-img-${index}`}
                      ref={(el) => { if (el) imageRefs.current[index] = el; }}
                      src={fullUrl || thumbUrl} 
                      alt={`Post image ${index + 1}`}
                      className={styles.mainImage}
                      loading="eager"
                      decoding="async"
                      onLoad={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.opacity = '1';
                        target.style.visibility = 'visible';
                        target.style.display = 'block';
                        target.style.transform = 'translateZ(0)';
                      }}
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.backgroundColor = 'var(--background-tertiary)';
                        (e.target as HTMLImageElement).style.display = 'block';
                      }}
                    />
                  );
                })}
                </div>
                
                {/* Для вертикальных изображений показываем информацию о пользователе поверх фото */}
                {isVerticalImage && (
                  <>
                    <div className={styles.imageOverlay}>
                      {renderUserInfo(true)}
                      {isOwnPost && !hideMenuButton && (
                        <div className={styles.menuContainerOverlay}>
                          <button 
                            className={styles.menuButtonOverlay}
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowMenu(!showMenu);
                            }}
                          >
                            <i className="ri-more-fill"></i>
                          </button>

                          {showMenu && (
                            <div className={styles.menuDropdownOverlay}>
                              {onEdit && (
                                <button
                                  className={styles.menuItem}
                                  onClick={() => { onEdit(post); setShowMenu(false); }}
                                >
                                  <i className="ri-edit-line"></i>
                                  Редактировать
                                </button>
                              )}
                              <button
                                className={styles.menuItem}
                                onClick={() => {
                                  onDelete(post.id);
                                  setShowMenu(false);
                                }}
                              >
                                <i className="ri-delete-bin-line"></i>
                                Удалить
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    {/* Область с текстом и миниатюрами для вертикальных изображений */}
                    {isVerticalImage && (imagesCount > 1 || post.content) && (
                      <div className={styles.thumbnailsContainerOverlay}>
                        {/* Текст описания внутри области миниатюр */}
                        {post.content && showTextOverlay && (
                          <div className={styles.textOverlayInline}>
                            <p>{renderContentWithHashtags(post.content)}</p>
                          </div>
                        )}
                        {/* Горизонтальная строка с миниатюрами и кнопкой */}
                        <div className={styles.thumbnailsRow}>
                          {/* Контейнер для миниатюр - центрируется */}
                          <div className={styles.thumbnailsContainer}>
                            {/* Миниатюры - показываем только если больше одного изображения */}
                            {imagesCount > 1 && (
                              <>
                                {thumbUrls.map((thumbUrl, index) => (
                                  <img
                                    key={index}
                                    src={thumbUrl}
                                    alt={`Thumbnail ${index + 1}`}
                                    className={`${styles.thumbnailOverlay} ${index === currentImageIndex ? styles.thumbnailOverlayActive : ''}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setCurrentImageIndex(index);
                                      // При клике на миниатюру открываем большой слайдер
                                      const imgElement = imageRefs.current[index];
                                      if (imgElement) {
                                        setSourceImageElement(imgElement);
                                        setSelectedImageIndex(index);
                                      }
                                    }}
                                  />
                                ))}
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
                
                {/* Кнопки навигации */}
                {imagesCount > 1 && (
                  <>
                    <button 
                      className={styles.sliderNavButton}
                      style={{ left: '10px' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        setCurrentImageIndex((prev) => (prev > 0 ? prev - 1 : imagesCount - 1));
                      }}
                      onTouchStart={(e) => e.stopPropagation()}
                      onTouchEnd={(e) => e.stopPropagation()}
                      aria-label="Предыдущее фото"
                    >
                      <i className="ri-arrow-left-s-line"></i>
                    </button>
                    <button 
                      className={styles.sliderNavButton}
                      style={{ right: '10px' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        setCurrentImageIndex((prev) => (prev < imagesCount - 1 ? prev + 1 : 0));
                      }}
                      onTouchStart={(e) => e.stopPropagation()}
                      onTouchEnd={(e) => e.stopPropagation()}
                      aria-label="Следующее фото"
                    >
                      <i className="ri-arrow-right-s-line"></i>
                    </button>
                  </>
                )}
                
                {/* Кнопка участников - в углу фото, поверх изображения */}
                {post.siteId && (
                  <button
                    className={styles.participantsButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowParticipantsSheet(true);
                    }}
                    title="Участники объекта"
                  >
                    <i className="ri-group-line"></i>
                  </button>
                )}
                {/* Кнопка показа/скрытия текста - справа, симметрично кнопке участников */}
                {isVerticalImage && post.content && (
                  <button
                    className={styles.textToggleButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowTextOverlay(!showTextOverlay);
                    }}
                    title={showTextOverlay ? "Скрыть описание" : "Показать описание"}
                  >
                    <i className={showTextOverlay ? "ri-file-text-fill" : "ri-file-text-line"}></i>
                  </button>
                )}
              </div>
              
              {/* Миниатюры - для обычных изображений показываем под фото */}
              {!isVerticalImage && (
                <div className={styles.thumbnailsContainer}>
                  {thumbUrls.map((thumbUrl, index) => (
                    <img
                      key={index}
                      src={thumbUrl}
                      alt={`Thumbnail ${index + 1}`}
                      className={`${styles.thumbnail} ${index === currentImageIndex ? styles.thumbnailActive : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setCurrentImageIndex(index);
                        // При клике на миниатюру открываем большой слайдер
                        const imgElement = imageRefs.current[index];
                        if (imgElement) {
                          setSourceImageElement(imgElement);
                          setSelectedImageIndex(index);
                        }
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
            
            {/* Для вертикальных изображений текст больше не показываем внизу - он в overlay */}
            
            {selectedImageIndex !== null && (
              <ImageViewer
                images={fullUrls}
                imagesThumb={thumbUrls}
                initialIndex={selectedImageIndex}
                onClose={handleCloseSlider}
                sourceElement={sourceImageElement}
                onIndexChange={handleIndexChange}
                siteId={post.siteId}
                onShowParticipants={() => setShowParticipantsSheet(true)}
              />
            )}
          </>
        );
      })()}

      <hr className={styles.actionsDivider} />

      <div className={styles.postActions}>
        <button 
          className={`${styles.actionButton} ${post.isLiked ? styles.liked : ''}`}
          onClick={() => onLike(post.id)}
        >
          <i className={post.isLiked ? "ri-heart-fill" : "ri-heart-line"}></i>
          {post.likes > 0 && <span>{post.likes}</span>}
        </button>
        
        <button 
          className={styles.actionButton}
          onClick={() => setShowComments(!showComments)}
        >
          <i className="ri-chat-3-line"></i>
          {post.comments > 0 && <span>{post.comments}</span>}
        </button>
        
        <button 
          className={styles.actionButton}
          title="Просмотры"
          disabled
        >
          <i className="ri-eye-line"></i>
          {viewsCount > 0 && <span>{viewsCount}</span>}
        </button>
        
        <button className={styles.actionButton} title="Поделиться">
          <i className="ri-share-forward-line"></i>
        </button>
      </div>

      <hr className={styles.actionsDivider} />

      {showComments && (
        <div className={styles.commentsSection}>
          <div className={styles.emptyComments}>
            <i className="ri-chat-3-line"></i>
            <p>Пока нет комментариев</p>
            <span>Будьте первым, кто оставит комментарий!</span>
          </div>
        </div>
      )}

      {/* Боттом-шит участников */}
      {post.siteId && (
        <PostParticipantsBottomSheet
          isOpen={showParticipantsSheet}
          onClose={() => setShowParticipantsSheet(false)}
          siteId={post.siteId}
        />
      )}
    </div>
  );
};

export default Post;

